import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useCurrentRegion } from './hooks';
import { LocationModalInput } from './parts/LocationModalInputContainer';
import { getCitiesStepData, getRegionsStepData } from './utils';
import { requestCurrentRegion } from '../../../actions/currentRegion';
import { closeLocationModal } from '../../../actions/locationModal';
import { LocationModalFooter, LocationModal, LocationModalContent } from '../../../components/LocationModal/desktop';
import { selectIsLocationModalOpen } from '../../../selectors/locationModal';
import { selectRegions } from '../../../selectors/regions';
import { TLocationModalStep } from '../../../types/locationModal';
import { TThunkDispatch } from '../../../types/redux';
import { IRegion } from '../../../types/regions';
import { getRegionsById } from '../../../utils/regions';

export const LocationModalContainerDesktop: React.FC = () => {
  const isOpen = useSelector(selectIsLocationModalOpen);
  const regions = useSelector(selectRegions);

  const regionsById = React.useMemo(() => getRegionsById(regions), [regions]);
  const currentRegion = useCurrentRegion(regionsById);

  const [modalRegion, setModalRegion] = React.useState<IRegion>(currentRegion);
  const [step, setStep] = React.useState<TLocationModalStep>('regions');

  const dispatch: TThunkDispatch = useDispatch();

  const handleClose = React.useCallback(() => {
    dispatch(closeLocationModal());
  }, [dispatch]);

  const handleRegionSelect = React.useCallback((region: IRegion) => {
    setModalRegion(region);
    setStep('cities');
  }, []);

  const handleBackClick = React.useCallback(() => {
    setStep('regions');
    setModalRegion(currentRegion);
  }, [currentRegion]);

  const handleSubmit = React.useCallback(() => {
    dispatch(closeLocationModal());
    dispatch(requestCurrentRegion(modalRegion));
  }, [dispatch, modalRegion]);

  const regionsStepData = React.useMemo(() => getRegionsStepData(regions), [regions]);
  const citiesStepData = React.useMemo(
    () => getCitiesStepData(regions, modalRegion, regionsById),
    [regions, modalRegion, regionsById],
  );

  const mainRegions = step === 'regions' ? regionsStepData.mainRegions : citiesStepData.mainCities;
  const regionsByColumns = step === 'cities' ? citiesStepData.citiesByColumns : regionsStepData.regionByColumns;

  if (!isOpen) {
    return null;
  }

  return (
    <LocationModal
      top={<LocationModalInput onRegionSelect={handleRegionSelect} />}
      content={
        <LocationModalContent
          step={step}
          currentRegion={modalRegion}
          mainRegions={mainRegions}
          regionColumns={regionsByColumns}
          onRegionClick={handleRegionSelect}
        />
      }
      bottom={<LocationModalFooter step={step} onBackClick={handleBackClick} onSubmit={handleSubmit} />}
      onClose={handleClose}
    />
  );
};
